import { computed, type Ref, ref } from 'vue'
import { defineStore } from 'pinia'
import { type CaseDTOV3, CaseStatusEnum } from '@/common/types/case'
import { PriorityCodeEnum } from '@/common/types/priorities'
import { type EmergencyCodeEnum } from '@/common/types/emergencies'
import { type ScreenDTOV3 } from '@/common/types/screen'
import type { CustomerDTOV4 } from '@/common/types/customer'

export const useCaseStore = defineStore('case', () => {
  const id: Ref<number> = ref(0)
  const reference: Ref<string | undefined> = ref()
  const parent: Ref<number | undefined> = ref()
  const customerId: Ref<number | undefined> = ref()
  const customerFullName: Ref<string | null> = ref(null)
  const status: Ref<CaseStatusEnum> = ref(CaseStatusEnum.START)
  const wakeupDate: Ref<string | undefined> = ref()
  const priority: Ref<PriorityCodeEnum> = ref(PriorityCodeEnum.MEDIUM)
  const emergency: Ref<EmergencyCodeEnum | null> = ref(null)
  const assigneeUser: Ref<string | undefined> = ref()
  const dueDate: Ref<string> = ref('')
  const dueDateAlert: Ref<string | undefined> = ref()
  const currentSkill: Ref<string> = ref('')
  const typology: Ref<string | null> = ref(null)
  const teamCode = ref<UUID | undefined>(undefined)
  const ready: Ref<boolean> = ref(false)
  const loadingError: Ref<boolean> = ref(false)
  const spaceCode = ref<UUID>()
  const displayInteraction = ref<boolean>(true)
  const displayCaseTimer = ref<boolean>(false)
  const targetProcessingTime = ref<number | undefined>()
  const treatmentScreen = ref<ScreenDTOV3>()
  const caseLockedUser = ref<string | undefined>()

  const init = (caseResponse: CaseDTOV3) => {
    id.value = caseResponse.id
    reference.value = caseResponse.externalReference
    parent.value = caseResponse.parentId
    customerId.value = caseResponse.customerId
    status.value = caseResponse.status
    wakeupDate.value = caseResponse.wakeupDate
    priority.value = caseResponse.priorityCode
    assigneeUser.value = caseResponse.assignee
    dueDate.value = caseResponse.dueDate
    dueDateAlert.value = caseResponse.dueDateAlert
    currentSkill.value = caseResponse.currentSkillCode

    if (caseResponse.contactDriverCode) typology.value = caseResponse.contactDriverCode
    if (caseResponse.emergencyCode) emergency.value = caseResponse.emergencyCode
    if (caseResponse.teamCode) teamCode.value = caseResponse.teamCode
  }

  const setCustomerFullName = (customerData: CustomerDTOV4 | null) => {
    customerFullName.value = customerData?.firstName && customerData?.lastName ? `${customerData.firstName} ${customerData.lastName}` : null
  }

  const readonly = computed(() => {
    return !!caseLockedUser.value
  })

  return {
    id,
    reference,
    parent,
    customerId,
    customerFullName,
    priority,
    emergency,
    status,
    assigneeUser,
    dueDate,
    dueDateAlert,
    currentSkill,
    typology,
    wakeupDate,
    teamCode,
    spaceCode,
    displayInteraction,
    displayCaseTimer,
    targetProcessingTime,
    treatmentScreen,
    ready,
    loadingError,
    readonly,
    caseLockedUser,
    setCustomerFullName,
    init,
  }
})
